import type {Repository} from '@github-ui/current-repository'
import {useCurrentUser} from '@github-ui/current-user'
import {DiffFileHeader} from '@github-ui/diff-file-header'
import {DiffLines} from '@github-ui/diff-lines'
import {Box} from '@primer/react'
import React, {useEffect, useMemo, useRef, useState} from 'react'

import {type ContextLineRange, useLoadInjectedContextLines} from '../../hooks/use-load-injected-context-lines'
import type {DiffEntryDataWithExtraInfo} from '../../types/commit-types'
import type {CurrentUserExtended} from '../../types/shared'
import {
  filesChangedHeaderHeightNumber,
  fileStickyHeaderMargin,
  mobileFilesChangedHeaderHeightNumber,
  stickyDiffHeaderSx,
} from '../../utils/sticky-header-offset'
import BlobActionsMenu from './BlobActionsMenu'

interface DiffProps {
  diffEntryData: React.MutableRefObject<DiffEntryDataWithExtraInfo[]>
  splitPreference: 'split' | 'unified'
  contextLinePathURL: string
  recalcTotalHeightOfVirtualWindow: () => void
  index: number
  helpUrl: string
  headerStickyOffset?: number
  repo: Repository
  oid: string
}

export const Diff = React.memo(React.forwardRef(DiffUnmemoized))

export function DiffUnmemoized({
  diffEntryData,
  splitPreference,
  contextLinePathURL,
  recalcTotalHeightOfVirtualWindow,
  helpUrl,
  headerStickyOffset = 0,
  repo,
  oid,
  index,
}: DiffProps) {
  const currentUser = useCurrentUser() as CurrentUserExtended | undefined
  const [diffEntryToUse, setDiffEntryToUse] = useState(diffEntryData.current[index]!)
  const [isCollapsed, setIsCollapsed] = useState(diffEntryToUse.collapsed)
  const toggleCollapse = () => {
    diffEntryToUse.collapsed = !isCollapsed
    diffEntryData.current[index]!.collapsed = !isCollapsed
    setIsCollapsed(!isCollapsed)
  }
  const hasExpandedAllRanges = useRef(false)
  const getLines = useLoadInjectedContextLines()
  const expandedRangesArray = useRef<ContextLineRange[]>([])

  const dealWithInjectedLines = async (range: ContextLineRange) => {
    expandedRangesArray.current.push(range)
    const response = await getLines(expandedRangesArray.current, contextLinePathURL, diffEntryToUse.pathDigest)
    if (response !== undefined) {
      const prevDiffData = {...diffEntryToUse}
      prevDiffData.diffLines = response.diffEntryWithContext
      setDiffEntryToUse(prevDiffData)
      diffEntryData.current[index] = prevDiffData
      recalcTotalHeightOfVirtualWindow()
    }
  }

  const expandAllContextLines = async () => {
    expandedRangesArray.current = []
    if (!hasExpandedAllRanges.current) {
      expandedRangesArray.current.push({
        start: 1,
        end: Math.max(diffEntryToUse.oldTreeEntry?.lineCount ?? 0, diffEntryToUse.newTreeEntry?.lineCount ?? 0),
      })
    }
    hasExpandedAllRanges.current = !hasExpandedAllRanges.current
    const response = await getLines(expandedRangesArray.current, contextLinePathURL, diffEntryToUse.pathDigest)
    if (response !== undefined) {
      const prevDiffData = {...diffEntryToUse}
      prevDiffData.diffLines = response.diffEntryWithContext
      setDiffEntryToUse(prevDiffData)
      diffEntryData.current[index] = prevDiffData
      recalcTotalHeightOfVirtualWindow()
    }
  }

  useEffect(() => {
    expandedRangesArray.current = []
  }, [diffEntryData])

  const canExpandOrCollapseLines = useMemo(() => {
    const firstDiffLineNumber = diffEntryData.current[index]?.diffLines?.[0]?.blobLineNumber || 0

    if (firstDiffLineNumber > 1) return true

    const initialDiffLineCount = diffEntryData.current[index]?.diffLines?.length || 0
    const lastDiffLineNumber = diffEntryData.current[index]?.diffLines?.[initialDiffLineCount - 1]?.blobLineNumber || 0
    const fileLineCount = diffEntryData.current[index]?.newTreeEntry?.lineCount || 0

    if (lastDiffLineNumber < fileLineCount) return true

    return false
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    diffEntryData.current[index]?.diffLines,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    diffEntryData.current[index]?.newTreeEntry?.lineCount,
    index,
    diffEntryData,
  ])

  return (
    <div className="position-relative">
      <Box
        sx={{
          ...stickyDiffHeaderSx,
          top: [
            fileStickyHeaderMargin(mobileFilesChangedHeaderHeightNumber, headerStickyOffset),
            fileStickyHeaderMargin(filesChangedHeaderHeightNumber, headerStickyOffset),
          ],
        }}
      >
        <DiffFileHeader
          areLinesExpanded={hasExpandedAllRanges.current}
          canExpandOrCollapseLines={canExpandOrCollapseLines}
          fileLinkHref={`#diff-${diffEntryToUse.pathDigest}`}
          isCollapsed={isCollapsed}
          linesAdded={diffEntryToUse.linesAdded}
          linesDeleted={diffEntryToUse.linesDeleted}
          newMode={diffEntryToUse.newTreeEntry?.mode}
          newPath={diffEntryToUse.newTreeEntry?.path}
          oldMode={diffEntryToUse.oldTreeEntry?.mode}
          oldPath={diffEntryToUse.oldTreeEntry?.path}
          patchStatus={diffEntryToUse.status}
          path={diffEntryToUse.path}
          onToggleExpandAllLines={expandAllContextLines}
          onToggleFileCollapsed={toggleCollapse}
          rightSideContent={
            <BlobActionsMenu
              oid={oid}
              path={diffEntryToUse.path}
              repo={repo}
              isViewable={!diffEntryToUse.isSubmodule}
            />
          }
        />
      </Box>
      {!isCollapsed && (
        <div className="border position-relative rounded-bottom-2">
          <DiffLines
            diffEntryData={diffEntryToUse}
            baseHelpUrl={helpUrl}
            commentingEnabled={false}
            commentBatchPending={false}
            repositoryId={repo.id.toString()}
            //the subject and subjectID are PR specific things, but we can likely use them for our
            //commenting implementation as well
            subject={{}}
            subjectId={'TODO'}
            viewerData={{
              avatarUrl: currentUser?.avatarURL ?? '',
              diffViewPreference: splitPreference,
              isSiteAdmin: false,
              login: currentUser?.login ?? '',
              tabSizePreference: currentUser?.tabSize ?? 8,
              //TODO: figure out what exactly leads to someone not being able to comment on a diff they can view..
              //is that a pr only thing?
              viewerCanComment: false,
              //i think this is just false for diffs since suggestions don't exist in a diff context
              viewerCanApplySuggestion: false,
            }}
            addInjectedContextLines={dealWithInjectedLines}
            commentingImplementation={undefined}
            diffLinesManuallyUnhidden={false}
            onHandleLoadDiff={() => {}}
          />
        </div>
      )}
    </div>
  )
}

try{ Diff.displayName ||= 'Diff' } catch {}
try{ DiffUnmemoized.displayName ||= 'DiffUnmemoized' } catch {}